'use client';

import React from 'react';
import { createPortal } from 'react-dom';
import { Plus, X, Trash2, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ConversationItem } from '@/lib/conversation-api';

type ConversationDrawerProps = {
  open: boolean;
  conversations: ConversationItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onNewChat: () => void | Promise<void>;
  onClose: () => void;
};

export function ConversationDrawer({
  open,
  conversations,
  selectedId,
  onSelect,
  onDelete,
  onNewChat,
  onClose,
}: ConversationDrawerProps) {
  React.useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  const sheet = (
    <div className='fixed inset-0 z-50'>
      <button
        type='button'
        className='absolute inset-0 bg-black/40'
        aria-label='닫기'
        onClick={onClose}
      />

      <div className='absolute inset-x-0 bottom-0 mx-auto w-full max-w-lg rounded-t-3xl bg-card p-4 shadow-2xl'>
        <div className='flex items-center justify-between pb-2'>
          <span className='text-foreground text-base font-semibold'>대화 목록</span>
          <div className='flex items-center gap-1'>
            <button
              type='button'
              onClick={() => {
                onNewChat();
                onClose();
              }}
              className='hover:bg-secondary/80 flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-primary transition-colors'
            >
              <Plus className='h-4 w-4' />
              새 대화
            </button>
            <button
              type='button'
              className='hover:bg-secondary/80 rounded-full p-2 transition-colors'
              aria-label='닫기'
              onClick={onClose}
            >
              <X className='h-5 w-5 text-muted-foreground' />
            </button>
          </div>
        </div>

        <div className='mt-2 flex max-h-80 flex-col gap-1 overflow-y-auto'>
          {conversations.length === 0 ? (
            <div className='flex flex-col items-center gap-2 py-8 text-muted-foreground'>
              <MessageSquare className='h-8 w-8 opacity-40' />
              <span className='text-sm'>대화 내역이 없습니다</span>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.conversationId}
                className={cn(
                  'group flex items-center justify-between rounded-2xl px-4 py-3 transition-colors',
                  conv.conversationId === selectedId
                    ? 'bg-primary/10 text-primary'
                    : 'hover:bg-secondary/60 text-foreground',
                )}
              >
                <button
                  type='button'
                  className='flex min-w-0 flex-1 flex-col items-start text-left'
                  onClick={() => onSelect(conv.conversationId)}
                >
                  <span className='w-full truncate text-sm font-medium'>{conv.title}</span>
                  <span className='text-muted-foreground text-xs'>{formatDate(conv.updatedAt)}</span>
                </button>
                <button
                  type='button'
                  className='ml-2 rounded-full p-1.5 opacity-0 transition-opacity hover:bg-destructive/10 group-hover:opacity-100'
                  aria-label='대화 삭제'
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(conv.conversationId);
                  }}
                >
                  <Trash2 className='h-4 w-4 text-destructive' />
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(sheet, document.body) : null;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const days = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return '오늘';
  if (days === 1) return '어제';
  if (days < 7) return `${days}일 전`;
  return date.toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' });
}
